// core/lib/shareTargets.ts — the share-sheet MODEL, shared by every share dialog.
//
// Extracted in 1.24.00 E1 from components/PostCard/SharePostDialog.tsx, where
// `buildPlatformTargets` and the ≤280-char note helpers were born (1.23.90).
// The profile dialog now wants the same six anchors and the same note, and a
// second copy of these query shapes is exactly how two share sheets start
// disagreeing about what a link looks like. One rule, one place.
//
// Everything here is PURE — no DOM, no React, no window — so both dialogs get
// byte-identical hrefs and a single test file can pin every query shape.

import type { IconName } from '@/icons';

/** Tile ink/disc tone. Brand tones are the platforms' own colours (paired with
 *  white ink at ≥3:1); the rest come from theme tokens so they hold in both themes. */
export type Tone = 'neutral' | 'accent' | 'x' | 'whatsapp' | 'telegram' | 'facebook' | 'reddit';

export interface ShareTarget {
  key: string;
  /** Visible label under the disc. */
  label: string;
  /** Accessible name — always contains the visible label (WCAG label-in-name). */
  ariaLabel: string;
  icon: IconName;
  tone: Tone;
  href: string;
  /** Email opens in the same tab (mailto:), everything else in a new one. */
  newTab: boolean;
}

/**
 * The platform anchors. Pure so the hrefs are deterministic given (url, title,
 * message). Every interpolated value is encodeURIComponent'd. `text=` is OMITTED
 * (not sent empty) on X and Telegram when there is no message.
 *
 * Discord is deliberately absent: it has no share URL.
 */
export function buildPlatformTargets(url: string, title: string, message: string): ShareTarget[] {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const m = message ? encodeURIComponent(message) : '';
  return [
    {
      key: 'x',
      label: 'X',
      ariaLabel: 'Share on X',
      icon: 'logo-x',
      tone: 'x',
      href: `https://x.com/intent/post?${m ? `text=${m}&` : ''}url=${u}`,
      newTab: true,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      ariaLabel: 'Share on WhatsApp',
      icon: 'logo-whatsapp',
      tone: 'whatsapp',
      href: `https://wa.me/?text=${encodeURIComponent(message ? `${message} ${url}` : url)}`,
      newTab: true,
    },
    {
      key: 'telegram',
      label: 'Telegram',
      ariaLabel: 'Share on Telegram',
      icon: 'logo-telegram',
      tone: 'telegram',
      href: `https://t.me/share/url?url=${u}${m ? `&text=${m}` : ''}`,
      newTab: true,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      ariaLabel: 'Share on Facebook',
      icon: 'logo-facebook',
      tone: 'facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      newTab: true,
    },
    {
      key: 'reddit',
      label: 'Reddit',
      ariaLabel: 'Share on Reddit',
      icon: 'logo-reddit',
      tone: 'reddit',
      href: `https://www.reddit.com/submit?url=${u}&title=${m || t}`,
      newTab: true,
    },
    {
      key: 'email',
      label: 'Email',
      ariaLabel: 'Share by Email',
      icon: 'mail',
      tone: 'accent',
      href: `mailto:?subject=${t}&body=${encodeURIComponent(message ? `${message}\n\n${url}` : url)}`,
      newTab: false,
    },
  ];
}

/** The optional note's ceiling, shared by every share sheet. */
export const SHARE_MESSAGE_MAX = 280;

/**
 * What the polite live region says for a message of `len` characters. A live
 * region only speaks when its text CHANGES, so this returns the SAME string
 * across a whole band of lengths: nothing for the first 19 characters, then one
 * line per 20 typed, then every character once 20 or fewer remain — roughly 33
 * announcements across the full range instead of 280. The visible "N / 280"
 * counter still updates on every keystroke; it just isn't the live region.
 */
export function shareCounterAnnouncement(len: number): string {
  if (len === 0) return '';
  const remaining = SHARE_MESSAGE_MAX - len;
  if (remaining === 0) return 'Message limit reached';
  if (remaining <= 20) return `${remaining} character${remaining === 1 ? '' : 's'} left`;
  const milestone = Math.floor(len / 20) * 20;
  return milestone === 0 ? '' : `${milestone} of ${SHARE_MESSAGE_MAX} characters used`;
}
